import { memo, useState } from "react";
import PropTypes from "prop-types";
import "./DepartureDateSidePanelContent.scss";
import Button, { BUTTON_SIZES, BUTTON_VARIANTS } from "app/pages/.shared/form/Button";
import CalendarList from "app/pages/.shared/CalendarList/CalendarList";
import { FormattedMessage } from "react-intl";
import { getMonthsYearsInDateInterval } from "app/utils/utils";
import compact from "lodash/compact";
import { isBefore, isSameDay } from "date-fns";
import className from "classnames";
import Typography, { TYPOGRAPHY_VARIANTS } from "app/pages/.shared/Typography/Typography";
import DepartureDateSidePanelSelectedDate from "app/pages/SmartDP/Search/DepartureDateSidePanelContent/DepartureDateSidePanelSelectedDate";
import { ICON_SIZES } from "app/constants";
import CrossCircleFilledIcon from "app/icons/navigation/CrossCircleFilledIcon";

const DepartureDateSidePanelContent = ({
	initialDate,
	onDateSelect,
	onDateConfirm,
	departureDateMin,
	departureDateMax,
	checkDateAvailability,
	isCalendarLoading,
	displayCalendarIcon,
	calendarDisabledView,
	departureDateLabel,
	returnDateLabel,
}) => {
	const [selectedDate, setSelectedDate] = useState(compact(initialDate));

	const handleDateSelect = date => {
		let newDate;
		// if selectedDate(departDate, endDate) exists, reset selectedDate
		if (selectedDate.length >= 2) {
			newDate = [].concat(date);
		} else if (selectedDate.length === 1) {
			const departure = new Date(selectedDate[0]);
			if (isBefore(date, departure) || isSameDay(date, departure)) {
				// Anterior or same date → new departure
				newDate = [].concat(date);
			} else {
				// Posterior date → set as return
				newDate = [...selectedDate, date];
			}
		} else {
			newDate = [...selectedDate, date];
		}
		setSelectedDate(newDate);
		onDateSelect(newDate);
	};

	const handleDateConfirmation = () => {
		onDateConfirm(selectedDate);
	};

	let months = [];
	if (departureDateMin && departureDateMax) {
		months = getMonthsYearsInDateInterval(departureDateMin, departureDateMax);
	}

	const handleCleanDepartureDate = () => {
		setSelectedDate([]);
		onDateSelect([]);
	};

	const departureDate = selectedDate[0];
	const endDate = selectedDate[1];

	const handleCleanArrivalDate = () => {
		const newDate = [].concat(departureDate);
		setSelectedDate(newDate);
		onDateSelect(newDate);
	};

	if (calendarDisabledView) {
		return calendarDisabledView;
	} else if (isCalendarLoading) {
		// avoid displaying something when in loading state before dates appears.
		// It is fast enough to avoid displaying a spinner
		return null;
	}
	return (
		<div className="departure-date-side-panel-content">
			<header className="departure-date-side-panel-content__header">
				<div className="departure-date-side-panel-content__dates-selected">
					<div
						className={className({
							"departure-date-side-panel-content__date-selected": true,
							"departure-date-side-panel-content__date-selected--has-value": departureDate,
						})}
					>
						<div>
							<Typography
								className="departure-date-side-panel-content__date-selected-label"
								variant={TYPOGRAPHY_VARIANTS.XSMALL}
							>
								{departureDateLabel}
							</Typography>

							<Typography
								className="departure-date-side-panel-content__date-selected-value"
								variant={TYPOGRAPHY_VARIANTS.REGULAR}
							>
								{departureDate ? (
									<DepartureDateSidePanelSelectedDate date={departureDate} />
								) : (
									<FormattedMessage id="sdp.search.departure.date.input.placeholder" />
								)}
							</Typography>
						</div>
						{departureDate && (
							<div
								className="departure-date-side-panel-content__date-selected-clear-icon"
								onClick={handleCleanDepartureDate}
							>
								<CrossCircleFilledIcon
									height={ICON_SIZES.MD}
									width={ICON_SIZES.MD}
								/>
							</div>
						)}
					</div>
					<div
						className={className({
							"departure-date-side-panel-content__date-selected": true,
							"departure-date-side-panel-content__date-selected--has-value": endDate,
							"departure-date-side-panel-content__date-selected--disabled": !departureDate,
						})}
					>
						<div>
							<Typography
								className="departure-date-side-panel-content__date-selected-label"
								variant={
									endDate || departureDate
										? TYPOGRAPHY_VARIANTS.XSMALL
										: TYPOGRAPHY_VARIANTS.REGULAR
								}
							>
								{returnDateLabel}
							</Typography>

							<Typography
								className="departure-date-side-panel-content__date-selected-value"
								variant={TYPOGRAPHY_VARIANTS.REGULAR}
							>
								{endDate && <DepartureDateSidePanelSelectedDate date={endDate} />}
								{Boolean(departureDate) && !endDate && (
									<FormattedMessage id="sdp.search.return.date.input.placeholder" />
								)}
							</Typography>
						</div>
						{endDate && (
							<div
								className="departure-date-side-panel-content__date-selected-clear-icon"
								onClick={handleCleanArrivalDate}
							>
								<CrossCircleFilledIcon
									height={ICON_SIZES.MD}
									width={ICON_SIZES.MD}
								/>
							</div>
						)}
					</div>
				</div>
			</header>

			<div className="departure-date-side-panel-content__body">
				<CalendarList
					heightOffset={254} // header + footer with CTA + selection overview
					selectedDate={selectedDate}
					months={months}
					onDateSelect={handleDateSelect}
					departureDateMin={departureDateMin}
					departureDateMax={departureDateMax}
					checkDateAvailability={checkDateAvailability}
					displayCalendarIcon={displayCalendarIcon}
				/>
			</div>

			<footer className="departure-date-side-panel-content__footer">
				<Button
					variant={BUTTON_VARIANTS.FILLED}
					size={BUTTON_SIZES.LARGE}
					disabled={selectedDate.length !== 2}
					onClick={handleDateConfirmation}
				>
					<FormattedMessage id="sdp.search.departure.date.side.panel.cta" />
				</Button>
			</footer>
		</div>
	);
};

DepartureDateSidePanelContent.propTypes = {
	onDateSelect: PropTypes.func,
	onDateConfirm: PropTypes.func,
	initialDate: PropTypes.array,
	departureDateMin: PropTypes.number,
	departureDateMax: PropTypes.number,
	checkDateAvailability: PropTypes.func,
	isCalendarLoading: PropTypes.bool,
	calendarDisabledView: PropTypes.element,
	departureDateLabel: PropTypes.object,
	returnDateLabel: PropTypes.object,
	displayCalendarIcon: PropTypes.bool,
};

export default memo(DepartureDateSidePanelContent);
